/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import {
  ALLOWED_INPUTS,
  launchedSchema,
  placementsOut,
  NOTHING_FOUND,
  foundSchema,
  placementSchema,
  type AllowedInput,
  type Placement,
} from '@/api/agreements';
import { request, requestFile } from '@/api/client';
import { DOCX_TYPE, MAX_DOCUMENT_BYTES, PDF_TYPE } from '@/api/imports';

/**
 * An agreement on the counterparty's paper.
 *
 * Every other agreement is Amida's own document, generated from its template.
 * This one is a file the other side drafted -- a PDF or a Word document --
 * uploaded when it is started and never edited, regenerated or filled in by
 * Vellum. What Vellum adds is where it is signed: a box on a PDF's page, or a
 * point in a Word document's words, the same way as on Amida's paper. The file
 * lives in the working store like any draft, and is read back through Vellum's
 * own permission check (`DOC-6`), never by a store's link.
 */

/** The kinds of box a PDF's page takes: no title or address, unlike the words. */
export const PAGE_SPOT_KINDS = ['signature', 'name', 'date', 'text'] as const;
export type PageSpotKind = (typeof PAGE_SPOT_KINDS)[number];

/**
 * One box on one page of a PDF, as kept.
 *
 * `x`, `y`, `width` and `height` are fractions of the page from its top-left
 * corner, so a box drawn on a page shown at any size means the same place;
 * `page_width` and `page_height` are the page's own size in points, for the
 * signature provider, which places its boxes in points. `allowed` is the rule
 * in force -- a name's letters included -- so nothing here needs the defaults
 * to read one.
 */
export const pageSpotSchema = z.object({
  party: z.enum(['company', 'counterparty']),
  kind: z.enum(PAGE_SPOT_KINDS),
  page: z.number(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  page_width: z.number(),
  page_height: z.number(),
  label: z.string().nullable(),
  allowed: z.enum(ALLOWED_INPUTS),
  /**
   * Which of the side's signers the box is for, from one (templates spec
   * §18). Always sent; left out or null, slot 1 (D-55).
   */
  slot: z.number().nullable().exactOptional(),
});

export type PageSpot = z.infer<typeof pageSpotSchema>;

/** A box on a signature line whose party the PDF's headings do not say. */
export const unsurePageSpotSchema = pageSpotSchema.omit({
  party: true,
  allowed: true,
  slot: true,
});

export type UnsurePageSpot = z.infer<typeof unsurePageSpotSchema>;

/**
 * What "Place them for me" offers on an Excel template's sheet, made a PDF: a
 * box on each labelled signature line its text layer draws, for the side its
 * heading names, and apart those whose side it does not. Not offered on a
 * counterparty's PDF (templates spec §20, D-62).
 */
export const pageFoundSchema = z.object({
  spots: z.array(pageSpotSchema),
  unsure: z.array(unsurePageSpotSchema),
});

export type PageFound = z.infer<typeof pageFoundSchema>;

/**
 * The counterparty's document on one agreement, and who signs it for them.
 *
 * `signer_name` and `signer_email` are null until the spots give the
 * counterparty a spot and name who signs for them. `signer_signs` says
 * whether that signer signs in Vellum -- exactly when they have a spot, and
 * always after Amida. `placed` says
 * where it is signed has been said; `can_change` whether the person reading
 * may still replace it or move its spots -- its owner, before it is sent. The
 * server decides again either way (`API-9`).
 */
export const counterpartyDocumentSchema = z.object({
  kind: z.enum(['pdf', 'docx']),
  source_version: z.number(),
  signer_name: z.string().nullable(),
  signer_email: z.string().nullable(),
  signer_signs: z.boolean(),
  page_spots: z.array(pageSpotSchema),
  placed: z.boolean(),
  can_change: z.boolean(),
});

export type CounterpartyDocument = z.infer<typeof counterpartyDocumentSchema>;

/**
 * A Word document drawn for placing its spots in its words, as the launch
 * form's preview is for Amida's paper -- the same markup, the same numbers,
 * so `PlaceSignatures` places on it unchanged.
 *
 * `placeable` false means the file holds something Vellum cannot carry into a
 * copy with tags in it -- an image, a footnote, tracked changes -- listed in
 * `not_carried`; such a document is replaced with a PDF of it instead.
 */
export const wordPreviewSchema = z.object({
  html: z.string(),
  document_digest: z.string(),
  placeable: z.boolean(),
  not_carried: z.array(z.string()),
  suggested: z.array(placementSchema),
  found: foundSchema.default(NOTHING_FOUND),
});

export type WordPreview = z.infer<typeof wordPreviewSchema>;

/* ── The file itself ─────────────────────────────────────────────────────── */

/** What the file picker offers: a PDF or a Word document, by name and type. */
export const THEIR_DOCUMENT_ACCEPT = ['.pdf', '.docx', PDF_TYPE, DOCX_TYPE].join(',');

/** Why a file of the wrong kind is not taken. */
export const NOT_THEIR_DOCUMENT =
  'Choose a PDF or a Word document (.docx). An older Word file (.doc) has to be saved as .docx or PDF first.';

/** Why a file that is too large is not taken. */
export const TOO_LARGE = 'The document may be at most 25 MB.';

/**
 * The type a file is sent as, or null if it is not a PDF or a .docx.
 *
 * By its name, in any case: a browser often has no type for a PDF, or calls
 * it `application/octet-stream`, and the server reads the bytes anyway.
 */
export function theirDocumentType(file: File): string | null {
  const name = file.name.trim().toLowerCase();
  if (name.endsWith('.pdf')) return PDF_TYPE;
  if (name.endsWith('.docx')) return DOCX_TYPE;
  return null;
}

/**
 * Why a file cannot be the counterparty's document, before it is sent, or null
 * if it can. The server checks again, from the bytes.
 */
export function theirDocumentProblem(file: File): string | null {
  if (theirDocumentType(file) === null) return NOT_THEIR_DOCUMENT;
  if (file.size === 0) return 'The file is empty.';
  if (file.size > MAX_DOCUMENT_BYTES) return TOO_LARGE;
  return null;
}

/** The file, sent as what its name says it is, whatever the browser guessed. */
function asSent(file: File): File {
  return new File([file], file.name, { type: theirDocumentType(file) ?? PDF_TYPE });
}

/* ── Starting one ────────────────────────────────────────────────────────── */

export interface TheirPaperInput {
  file: File;
  workflowId: string;
  title: string;
  counterpartyName: string;
  counterpartyKind?: 'person' | 'organization' | undefined;
  counterpartyEmail?: string | undefined;
  /** `YYYY-MM-DD`, or left out for none yet. */
  expiresAt?: string | undefined;
  /**
   * Who signs, one person a slot (templates spec §18, `WF-100`): Amida's
   * slots, and the counterparty's where they sign. Left out, nobody yet.
   */
  signers?: readonly LaunchSigner[] | undefined;
}

/** One person named for one slot at launch, as the `signers` form part takes it. */
export interface LaunchSigner {
  party: 'amida' | 'counterparty';
  slot: number;
  name: string;
  email: string;
}

/**
 * Start an agreement on the counterparty's document.
 *
 * Multipart, the file and the form together, in one request: the file is
 * stored as the agreement's version 1 and nothing is generated. Who signs for
 * Amida is named afterwards with `setSigners`, as on Amida's paper. Where it
 * is signed -- and who signs for the counterparty, if they are given a spot --
 * is said on the spots page.
 */
export const launchOnTheirPaper = (input: TheirPaperInput) => {
  const form = new FormData();
  form.append('file', asSent(input.file));
  form.append('workflow_id', input.workflowId);
  form.append('title', input.title.trim());
  form.append('counterparty_name', input.counterpartyName);
  if (input.counterpartyKind) form.append('counterparty_kind', input.counterpartyKind);
  if (input.counterpartyEmail) form.append('counterparty_email', input.counterpartyEmail);
  if (input.expiresAt) form.append('expires_at', input.expiresAt);
  if (input.signers && input.signers.length > 0) {
    // One form part, a JSON array: the route is multipart.
    form.append(
      'signers',
      JSON.stringify(
        input.signers.map((s) => ({
          party: s.party,
          slot: s.slot,
          name: s.name.trim(),
          email: s.email.trim(),
        })),
      ),
    );
  }
  return request('/api/v1/agreements/counterparty-paper', launchedSchema, {
    method: 'POST',
    body: form,
  });
};

/**
 * A Word document picked on the launch form, drawn by the server so it can be
 * read before anything is created -- the same drawing the spots page shows.
 * Nothing is stored: the server reads the bytes and drops them with the
 * request. A PDF is drawn in the browser instead (`openPdf`), and never sent.
 */
export const previewPickedWord = (file: File, signal?: AbortSignal) => {
  const form = new FormData();
  form.append('file', asSent(file));
  return request('/api/v1/agreements/counterparty-paper/preview', wordPreviewSchema, {
    method: 'POST',
    body: form,
    ...(signal ? { signal } : {}),
  });
};

/* ── The document on an agreement ────────────────────────────────────────── */

const documentPath = (id: string) => `/api/v1/agreements/${id}/counterparty-document`;

/**
 * The file as it was uploaded, through Vellum: "Download the document" links here,
 * and the PDF spot editor draws its pages from it.
 */
export const theirFilePath = (id: string) => `${documentPath(id)}/file`;

/**
 * What the document is and who signs it for them. Asked only of an agreement
 * on the counterparty's paper: any other answers 404.
 */
export const counterpartyDocumentQuery = (id: string) =>
  queryOptions({
    queryKey: ['agreement', id, 'counterparty-document'] as const,
    queryFn: ({ signal }) =>
      request(documentPath(id), counterpartyDocumentSchema, { signal }),
  });

/** The file's bytes, for drawing a PDF's pages on the page. */
export const theirFileBytes = (id: string, signal?: AbortSignal) =>
  requestFile(theirFilePath(id), signal ? { signal } : {});

/**
 * Replace the document before it is sent: a corrected file from them. It is
 * the agreement's next version -- the one before is never written over -- its
 * spots are cleared, and anybody who approved is asked again. Not optimistic:
 * the page waits for the answer, then reads everything again.
 */
export const replaceTheirDocument = (id: string, file: File) => {
  const form = new FormData();
  form.append('file', asSent(file));
  return request(documentPath(id), counterpartyDocumentSchema, {
    method: 'POST',
    body: form,
  });
};

/* ── Where it is signed ──────────────────────────────────────────────────── */

/**
 * One box as it is sent. `allowed` is left out where the kind's own rule
 * stands, and a `label` goes only with a field the person named; the server
 * refuses either where it does not belong.
 */
export interface PageSpotInput {
  party: 'company' | 'counterparty';
  kind: PageSpotKind;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  page_width: number;
  page_height: number;
  label?: string;
  allowed?: AllowedInput;
  /** Which of the side's signers, from one; left out for slot 1 (D-55). */
  slot?: number;
}

/**
 * Who signs for the counterparty, as the spots page sends it: asked only once
 * the counterparty is given a spot, and then required.
 */
export const counterpartySignerSchema = z.object({
  name: z.string(),
  email: z.string(),
});

export type CounterpartySignerInput = z.infer<typeof counterpartySignerSchema>;

/** The body's `signer`, only where somebody signs for the counterparty. */
const withSigner = (signer: CounterpartySignerInput | null) =>
  signer ? { signer: { name: signer.name.trim(), email: signer.email.trim() } } : {};

/**
 * Say where a PDF is signed, and who signs it, replacing every box there was.
 * One signature for Amida, always; the counterparty's spots are optional, and
 * with any of them `signer` is required and they sign after Amida. Fields only
 * beside a signature. The server refuses the lot, with words for why, rather
 * than keep the part that fitted.
 */
export const setPageSpots = (
  id: string,
  spots: readonly PageSpotInput[],
  signer: CounterpartySignerInput | null = null,
) =>
  request(`${documentPath(id)}/page-spots`, counterpartyDocumentSchema, {
    method: 'PUT',
    body: { spots, ...withSigner(signer) },
  });

/**
 * A Word document drawn with the spots chosen so far, as the launch form's
 * preview is: the server draws each where the sent copy will have it. Nothing
 * is saved.
 */
export const previewTheirWords = (id: string, placements: readonly Placement[]) =>
  request(`${documentPath(id)}/preview`, wordPreviewSchema, {
    method: 'POST',
    body: { placements: placementsOut(placements) },
  });

/**
 * Say where a Word document is signed, and who signs it: points in its words,
 * taken on the preview whose `document_digest` is sent with them. A copy of the
 * file with a tag at each becomes the version that is sent; the counterparty's
 * own file is never written over. `signer` as for `setPageSpots`.
 */
export const setWordSpots = (
  id: string,
  placements: readonly Placement[],
  documentDigest: string,
  signer: CounterpartySignerInput | null = null,
) =>
  request(`${documentPath(id)}/spots`, counterpartyDocumentSchema, {
    method: 'PUT',
    body: {
      placements: placementsOut(placements),
      document_digest: documentDigest,
      ...withSigner(signer),
    },
  });
